import React, { useCallback, useEffect, useState } from 'react';
import { Cloud, Download, RefreshCw, ShieldAlert } from 'lucide-react';

const DEFAULT_MANIFEST = '/downloads/local-version.json';
const ALLOWED_UPDATE_HOSTS = ['penguen.info', 'www.penguen.info'];

const compareVersions = (left, right) => {
  const a = String(left || '0').split('.').map((part) => Number(part) || 0);
  const b = String(right || '0').split('.').map((part) => Number(part) || 0);
  const length = Math.max(a.length, b.length);

  for (let index = 0; index < length; index += 1) {
    const diff = (a[index] || 0) - (b[index] || 0);
    if (diff !== 0) {
      return diff;
    }
  }

  return 0;
};

const isAllowedUpdateUrl = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === 'https:'
      && ALLOWED_UPDATE_HOSTS.includes(parsed.hostname)
      && parsed.pathname.startsWith('/downloads/');
  } catch {
    return false;
  }
};

const DesktopSyncBar = () => {
  const desktopInfo = window.ROYSIS_DESKTOP || null;
  const [manifest, setManifest] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [installing, setInstalling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const currentVersion = desktopInfo?.version || '1.0.0';
  const manifestUrl = desktopInfo?.updateManifestUrl || DEFAULT_MANIFEST;
  const supportsNativeInstaller = desktopInfo?.supportsInstallerBridge === '1';
  const hasWebViewBridge = Boolean(window.chrome?.webview?.postMessage);

  const checkUpdates = useCallback(async () => {
    setChecking(true);
    setError('');

    try {
      const response = await fetch(`${manifestUrl}?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error('Guncelleme bilgisi alinamadi');
      }
      const data = await response.json();
      setManifest(data);
    } catch (updateError) {
      setError(updateError.message || 'Sunucu kontrolu basarisiz');
    } finally {
      setChecking(false);
    }
  }, [manifestUrl]);

  useEffect(() => {
    if (!desktopInfo) {
      return undefined;
    }

    checkUpdates();
    const timer = window.setInterval(checkUpdates, 5 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [checkUpdates, desktopInfo]);

  useEffect(() => {
    const webview = window.chrome?.webview;
    if (!webview?.addEventListener) {
      return undefined;
    }

    const handleHostMessage = (event) => {
      let data = event?.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }

      if (!data?.type) {
        return;
      }

      if (data.type === 'updateProgress') {
        setInstalling(true);
        setError('');
        setProgress(Number(data.percent) || 0);
        setProgressMessage(data.message || 'Indiriliyor...');
      }

      if (data.type === 'updateDone') {
        setProgress(100);
        setProgressMessage(data.message || 'Kurulum baslatildi. Uygulama kapanacak...');
      }

      if (data.type === 'updateError') {
        setInstalling(false);
        setProgress(0);
        setProgressMessage('');
        setError(data.message || 'Guncelleme basarisiz');
      }
    };

    webview.addEventListener('message', handleHostMessage);
    return () => webview.removeEventListener('message', handleHostMessage);
  }, [desktopInfo]);

  useEffect(() => {
    if (!installing) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setInstalling(false);
      setProgress(0);
      setProgressMessage('');
      setError('Guncelleme yaniti alinamadi. ROYSIS uygulamasini tamamen kapatip tekrar acin; acilis penceresinden onaylayin.');
    }, 20000);

    return () => window.clearTimeout(timeout);
  }, [installing]);

  const triggerDesktopUpdate = () => {
    if (!manifest?.downloadUrl || !isAllowedUpdateUrl(manifest.downloadUrl)) {
      setError('Gecersiz guncelleme adresi');
      return;
    }

    if (!hasWebViewBridge) {
      setError('Masaustu guncelleme koprusu bulunamadi. Uygulamayi kapatip acin.');
      return;
    }

    setInstalling(true);
    setError('');
    setProgress(0);
    setProgressMessage('Guncelleme istegi gonderiliyor...');

    window.chrome.webview.postMessage(JSON.stringify({
      action: 'runUpdateScript',
    }));
  };

  if (!desktopInfo || desktopInfo.mode !== 'cloud') {
    return null;
  }

  const remoteVersion = manifest?.version;
  const updateAvailable = remoteVersion && compareVersions(remoteVersion, currentVersion) > 0;

  return (
    <div className="w-full shrink-0 border-b border-sky-200 bg-sky-50 px-4 py-2 text-xs text-sky-950 sm:text-sm">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          <Cloud className="h-4 w-4" />
          <span>Sunucu ile senkronize calisiyor</span>
          <span className="rounded bg-white px-2 py-0.5 text-[11px] font-bold text-sky-700">
            produced by penguen
          </span>
          <span className="rounded bg-white px-2 py-0.5 text-[11px] font-bold text-sky-700">
            v{currentVersion}
          </span>
          {remoteVersion && (
            <span className="text-sky-700">
              Sunucu surumu: v{remoteVersion}
            </span>
          )}
        </div>

        <div className="flex min-w-[240px] flex-1 flex-col items-end gap-1 sm:max-w-xl">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {checking && (
              <span className="inline-flex items-center gap-1 text-sky-700">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                Kontrol ediliyor...
              </span>
            )}
            {updateAvailable && !installing && (
              <button
                type="button"
                onClick={triggerDesktopUpdate}
                className="inline-flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-500"
              >
                <Download className="h-3.5 w-3.5" />
                Guncelle ve kur (v{remoteVersion})
              </button>
            )}
            {!updateAvailable && !checking && !error && !installing && (
              <span className="rounded bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700">
                Guncel
              </span>
            )}
          </div>

          {updateAvailable && !supportsNativeInstaller && (
            <p className="w-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold leading-5 text-amber-900">
              Masaustu surumunuz eski (v{currentVersion}). ROYSIS uygulamasini tamamen kapatip tekrar acin;
              acilis penceresinde <strong>Evet</strong> secin. Uygulama icinden indirme tarayiciya duser ve calismaz.
            </p>
          )}

          {updateAvailable && (
            <p className="flex w-full items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] leading-5 text-slate-700">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              <span>
                Windows SmartScreen uyarisi normaldir. Kurulumda <strong>Daha fazla bilgi</strong> &rarr; <strong>Yine de calistir</strong> secin.
                Dosya SHA256 ile dogrulanir.
              </span>
            </p>
          )}

          {error && (
            <p className="w-full rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-semibold leading-5 text-red-700">
              {error}
            </p>
          )}

          {installing && (
            <div className="w-full rounded-lg border border-sky-200 bg-white px-3 py-2">
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px] font-semibold text-sky-800">
                <span className="inline-flex items-center gap-1">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  {progressMessage || 'Guncelleme baslatiliyor...'}
                </span>
                <span>{Math.max(0, Math.min(100, progress))}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-sky-100">
                <div
                  className="h-full rounded-full bg-sky-600 transition-all duration-300"
                  style={{ width: `${Math.max(8, Math.min(100, progress))}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopSyncBar;
